import {
  Component,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { NbDialogService, NbPopoverDirective } from '@nebular/theme';

interface ResourceOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-single-select-search',
  templateUrl: './single-select-search.component.html',
  styleUrls: ['./single-select-search.component.scss'],
})
export class SingleSelectSearchComponent implements OnChanges {
  @Output() selectedChange = new EventEmitter<any>(); // Event emitter for selected option

  @Output() optionIconEvent = new EventEmitter<any>(); // Event emitter for selected option
  @Output() createNewOptionEvent = new EventEmitter<any>();

  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() tooltipText: string = ''; //tooltip text for label
  @Input() showInfoAsTooltip: boolean = true;
  @Input() labelSize: number = 3;
  @Input() inputSize: number = 5;
  @Input() options: { label: string; value: string }[] = [];
  @Input() groupedOptions: { [key: string]: string[] } = {};
  @Input() noOptionsMessage: string = `No ${this.label} available`;
  @Input() searchInputId: string = '';
  @Input() selectInputPlaceholder: string = `Select ${this.label}`;
  @Input() searchInputPlaceholder: string = `Search ${this.label}`;
  @Input() showClearOption: boolean = false;
  @Input() loading: boolean = false;
  @Input() disabled: boolean = false;
  @Input() disabledTooltip: string = ''; //tooltip on select input when input is disabled for any reason
  @Input() customFormControl: UntypedFormControl;
  @Input() formMargin: number = 4;
  @Input() showSuffixIcon: boolean = false;
  @Input() optionsIcon: string = '';
  @Input() createNewOptionLabel: string = '';
  @Input() categorySelection: boolean = false;

  @ViewChild('singleSelectSearchDropdownPopover', { static: false })
  singleSelectSearchDropdownPopover: NbPopoverDirective;

  openOptionsPopover: boolean = false;
  selectedOption: any | null = null;
  searchString: UntypedFormControl = new UntypedFormControl('');
  filteredOptions: { label: string; value: string }[] = [];
  filteredGroupedOptions: { [key: string]: ResourceOption[] } = {};
  allOptions: { [key: string]: ResourceOption[] } = {};

  constructor() {}

  ngOnChanges(changes: SimpleChanges) {
    if (this.options) {
      this.filteredOptions = [...this.options];
    }
    if (changes?.groupedOptions?.currentValue) {
      this.initializeGroupedOptions();
    }
  }

  initializeGroupedOptions() {
    Object.entries(this.groupedOptions).forEach(([type, values]) => {
      this.allOptions[type] = values?.map((value) => ({
        label: value,
        value: value,
      }));
    });
    this.filteredGroupedOptions = { ...this.allOptions };
  }

  getFilteredOptions() {
    const value = this.searchString?.value;
    if (typeof value === 'string') {
      this.searchString.setValue(value.trim());
    }
    const searchTerm = this.searchString?.value || '';
    const searchLower = searchTerm?.toLowerCase();
    if (!searchTerm) {
      if (this.categorySelection) {
        this.filteredGroupedOptions = { ...this.allOptions };
      } else {
        this.filteredOptions = [...this.options];
      }
      return;
    }
    if (this.categorySelection) {
      this.filteredGroupedOptions = Object.entries(this.allOptions)?.reduce(
        (acc, [category, resources]) => {
          const filteredResources = resources?.filter((resource) =>
            resource?.label?.toLowerCase()?.includes(searchLower),
          );
          if (filteredResources?.length > 0) {
            acc[category] = filteredResources;
          }
          return acc;
        },
        {} as { [key: string]: ResourceOption[] },
      );
    } else {
      this.filteredOptions = this.options?.filter((option) =>
        option?.label?.toLowerCase()?.includes(searchLower),
      );
    }
  }

  selectOption(option: { label: string; value: string }) {
    this.selectedOption = option;
    this.customFormControl.setValue(option?.value);
    this.emitSelectedEvent();
    this.singleSelectSearchDropdownPopover.hide();
  }

  selectGroupedOption(option: { label: string; value: string }, type: string) {
    this.selectedOption = { option, type };
    this.customFormControl.setValue(option?.value);
    this.emitSelectedEvent();
    this.singleSelectSearchDropdownPopover.hide();
  }

  clearSelectedOption(event) {
    event?.stopPropagation();
    this.selectedOption = null;
    this.customFormControl?.setValue(null);
    this.emitSelectedEvent();
  }

  emitSelectedEvent() {
    this.selectedChange.emit(this.selectedOption);
  }

  showStateChange(event) {
    this.openOptionsPopover = event.isShown;
    if (!event.isShown) {
      this.searchString.setValue('');
      this.getFilteredOptions();
    } else {
      this.openOptionsPopover = true;
      let selectBoundingRect = document
        .querySelector('.input-parent')
        .getBoundingClientRect();
      let popoverElem = document.querySelector('nb-popover');
      let dropdownElement = popoverElem?.parentElement;
      if (dropdownElement) {
        dropdownElement.style.width = selectBoundingRect.width + 'px';
        dropdownElement.style.left = selectBoundingRect.left + 'px';
      }
    }
  }

  optionIconClicked(event, option) {
    event?.stopPropagation();
    this.optionIconEvent.emit(option);
  }

  onCreateNewOption(event) {
    this.createNewOptionEvent.emit();
  }
}
